import React, { useState } from 'react';
import { CheckCircle, AlertCircle, Clock, ExternalLink, ChevronDown } from 'lucide-react';
import { formatCurrency } from '../../utils/helpers';

const QUARTERS = [
  { q: 1, due: 'Apr 15' },
  { q: 2, due: 'Jun 15' },
  { q: 3, due: 'Sep 15' },
  { q: 4, due: 'Jan 15' },
];

const QuarterlyTaxReport = ({ data, onQuarterChange }) => {
  const [showNumbers, setShowNumbers] = useState(false);
  const [showNotes, setShowNotes] = useState(false);

  if (!data) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p>No data available.</p>
      </div>
    );
  }

  const irsAmount = (data.estimatedSelfEmploymentTax || 0) + (data.estimatedFederalIncomeTax || 0);
  const stateAmount = data.estimatedStateIncomeTax || 0;
  const owes = (data.quarterlyPaymentEstimate || 0) > 0;

  const dueDate = new Date(data.estimatedTaxDueDate);
  const dueDay = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate());
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const daysUntil = Math.round((dueDay - today) / 86400000);
  const pastDue = daysUntil < 0;
  const dueLabel = dueDay.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  const soon = daysUntil >= 0 && daysUntil <= 30;

  const ytdLoss = data.ytdNetIncome <= 0;

  return (
    <div className="space-y-4">
      {/* Deadline picker */}
      <div className="flex flex-wrap gap-2 print:hidden">
        {QUARTERS.map(({ q, due }) => (
          <button
            key={q}
            onClick={() => onQuarterChange && onQuarterChange(q)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              data.quarter === q
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Due {due}
          </button>
        ))}
      </div>

      {/* The answer */}
      {!owes ? (
        <div className="card p-5 bg-green-50 border border-green-200">
          <div className="flex items-start gap-3">
            <CheckCircle className="text-green-500 flex-shrink-0" size={28} />
            <div>
              <p className="text-lg font-bold text-green-900">Nothing to pay for the {dueLabel} deadline</p>
              <p className="text-sm text-green-800 mt-1">
                {ytdLoss
                  ? `So far this year your expenses (${formatCurrency(data.ytdTotalDeductions)}) are more than your income (${formatCurrency(data.ytdGrossIncome)}). Tax is only owed on profit, so there is nothing to send in.`
                  : 'Based on your numbers so far, no payment is needed for this deadline.'}
              </p>
              <p className="text-xs text-green-700 mt-2">
                Keep recording invoices and expenses. This updates on its own, and it will tell you here when a payment is due.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className={`card p-5 border ${pastDue ? 'bg-red-50 border-red-200' : soon ? 'bg-amber-50 border-amber-200' : 'bg-indigo-50 border-indigo-200'}`}>
          <div className="flex items-start gap-3">
            {pastDue || soon
              ? <AlertCircle className={pastDue ? 'text-red-500 flex-shrink-0' : 'text-amber-500 flex-shrink-0'} size={28} />
              : <Clock className="text-indigo-500 flex-shrink-0" size={28} />}
            <div className="flex-1">
              <p className="text-sm text-gray-600">
                {pastDue ? 'This payment was due' : 'Pay by'} {dueLabel}
                {!pastDue && ` (${daysUntil} day${daysUntil !== 1 ? 's' : ''} from now)`}
              </p>
              <p className="text-3xl font-bold text-gray-900 mt-1">About {formatCurrency(irsAmount)}</p>
              <p className="text-sm text-gray-600">to the IRS</p>
              {stateAmount > 0 && (
                <p className="text-sm text-gray-700 mt-2">
                  Plus about <strong>{formatCurrency(stateAmount)}</strong> to your state (paid separately on your state's tax website).
                </p>
              )}
              {pastDue && (
                <p className="text-xs text-red-700 mt-2">
                  If you haven't paid this yet, pay as soon as you can. The IRS adds a small penalty and interest for each day it is late.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* How to pay: only when something is owed */}
      {owes && (
        <div className="card p-4">
          <h3 className="font-semibold text-gray-900 mb-3">How to pay the IRS (about 5 minutes)</h3>
          <ol className="space-y-3 text-sm text-gray-700">
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center">1</span>
              <span>
                Go to{' '}
                <a
                  href="https://www.irs.gov/payments/direct-pay"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 font-medium underline inline-flex items-center gap-1"
                >
                  IRS Direct Pay <ExternalLink size={12} />
                </a>
                . It is free and pays straight from your checking or savings account.
              </span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center">2</span>
              <span>
                Choose <strong>Make a Payment</strong>, pick <strong>Estimated Tax</strong> as the reason, and <strong>{data.year}</strong> as the tax year. The IRS menus change now and then, so follow what they show.
              </span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center">3</span>
              <span>
                Enter <strong>{formatCurrency(irsAmount)}</strong>, confirm it, and submit. Save the confirmation number and give it to your accountant.
              </span>
            </li>
          </ol>
        </div>
      )}

      {/* Numbers (collapsed) */}
      <div className="card p-0 overflow-hidden">
        <button
          onClick={() => setShowNumbers(!showNumbers)}
          className="w-full flex items-center justify-between p-3 hover:bg-gray-50"
        >
          <span className="text-sm font-medium text-gray-900">Show my numbers</span>
          <ChevronDown size={16} className={`text-gray-400 transition-transform ${showNumbers ? 'rotate-180' : ''}`} />
        </button>
        {showNumbers && (
          <div className="border-t border-gray-100 divide-y divide-gray-50 text-sm">
            <div className="flex justify-between px-3 py-2">
              <span className="text-gray-600">Money collected this year</span>
              <span className="font-semibold">{formatCurrency(data.ytdGrossIncome)}</span>
            </div>
            <div className="flex justify-between px-3 py-2">
              <span className="text-gray-600">Business expenses this year</span>
              <span className="font-semibold">{formatCurrency(data.ytdTotalDeductions)}</span>
            </div>
            <div className="flex justify-between px-3 py-2">
              <span className="text-gray-600">Profit so far this year</span>
              <span className={`font-semibold ${ytdLoss ? 'text-red-600' : 'text-gray-900'}`}>{formatCurrency(data.ytdNetIncome)}</span>
            </div>
            {owes && (
              <>
                <div className="flex justify-between px-3 py-2">
                  <span className="text-gray-600">Self-employment tax</span>
                  <span className="font-semibold">{formatCurrency(data.estimatedSelfEmploymentTax)}</span>
                </div>
                <div className="flex justify-between px-3 py-2">
                  <span className="text-gray-600">Federal income tax</span>
                  <span className="font-semibold">{formatCurrency(data.estimatedFederalIncomeTax)}</span>
                </div>
                {stateAmount > 0 && (
                  <div className="flex justify-between px-3 py-2">
                    <span className="text-gray-600">State income tax</span>
                    <span className="font-semibold">{formatCurrency(stateAmount)}</span>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Good to know (collapsed) */}
      <div className="card p-0 overflow-hidden">
        <button
          onClick={() => setShowNotes(!showNotes)}
          className="w-full flex items-center justify-between p-3 hover:bg-gray-50"
        >
          <span className="text-sm font-medium text-gray-900">Good to know</span>
          <ChevronDown size={16} className={`text-gray-400 transition-transform ${showNotes ? 'rotate-180' : ''}`} />
        </button>
        {showNotes && (
          <div className="border-t border-gray-100 p-3 space-y-2 text-xs text-gray-600">
            <p>• Self-employed people pay income tax four times a year instead of having it taken from a paycheck. The deadlines are Apr 15, Jun 15, Sep 15 and Jan 15. If one lands on a weekend or holiday, it moves to the next business day.</p>
            <p>• If you have a job where tax is taken out of your paycheck, that counts toward what you owe, and it may already cover you. The IRS generally wants you to pay at least 90% of this year's tax, or 100% of last year's (110% if last year's income was over $150,000), through withholding plus estimates. Check the Form 1040-ES instructions or your tax software.</p>
            <p>• Tax is owed on profit (money in minus expenses), not on everything you collect.</p>
            <p>• This assumes you paid the earlier deadlines. We don't track what you've actually paid, so if you skipped one, pay it too.</p>
            <p>• These are rough estimates. The Form 1040-ES worksheet gives the exact number, and your tax software shows at filing whether any penalty applies.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuarterlyTaxReport;